import { useRef, useEffect, useCallback } from 'react';

interface TextareaHeightConfig {
  minHeight: number;
  maxHeight: number;
  wrapperHeight: number;
  defaultRows: number;
  cssVariable?: string;
}

interface UseTextareaHeightReturn {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  adjustHeight: () => void;
  resetHeight: () => void;
}

export const useTextareaHeight = (
  config: TextareaHeightConfig
): UseTextareaHeightReturn => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback((): void => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '30px';

    const newHeight = Math.min(
      textarea.scrollHeight < config.minHeight
        ? config.minHeight
        : textarea.scrollHeight,
      config.maxHeight
    );
    textarea.style.height = `${newHeight}px`;

    if (config.cssVariable) {
      const totalHeight = newHeight + config.wrapperHeight;
      document.documentElement.style.setProperty(
        config.cssVariable,
        `${totalHeight}px`
      );
    }
  }, [
    config.minHeight,
    config.maxHeight,
    config.wrapperHeight,
    config.cssVariable,
  ]);

  const resetHeight = (): void => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    if (config.cssVariable) {
      document.documentElement.style.setProperty(
        config.cssVariable,
        `${config.wrapperHeight}px`
      );
    }
  };

  useEffect(
    () => () => {
      if (config.cssVariable) {
        document.documentElement.style.removeProperty(config.cssVariable);
      }
    },
    [config.cssVariable]
  );

  return {
    textareaRef,
    adjustHeight,
    resetHeight,
  };
};
